import { useCallback, useEffect, useRef } from 'react';

type DeckGlContextLossWorkaround = {
  /**
   * Pass to the Deck / MapboxOverlay constructor so the WebGL context can be
   * captured once it exists (device creation is async, so it cannot be read
   * from the overlay at mount time). Takes `unknown` because luma.gl's Device
   * type is not reachable through this app's direct dependencies.
   */
  onDeviceInitialized: (device: unknown) => void;
  /**
   * Call in an effect cleanup that is registered *after* the `useControl` call
   * creating the overlay: effect cleanups run in definition order, which
   * guarantees deck.finalize() has already run when the context is lost.
   */
  loseWebGlContext: () => void;
};

/**
 * Workaround for deck.gl leaking WebGL contexts on unmount, which OOMs iOS.
 *
 * deck.finalize() drops all references to its WebGL context but cannot destroy
 * it — WebGL has no API for that — so the context is left for the garbage
 * collector. WebKit only reclaims a context's GPU memory (textures, buffers)
 * on explicit context loss, so on iOS every mount/unmount cycle of a deck
 * overlay leaks an entire WebGL context. Forcing loss via the
 * WEBGL_lose_context extension releases the memory immediately.
 *
 * @see: Open bug in deck.gl: https://github.com/visgl/deck.gl/issues/10355
 * @see: Open fix for deck.gl: https://github.com/visgl/deck.gl/pull/10356
 * @linear:ENG-7241 https://linear.app/watchduty/issue/ENG-7241/address-ios-memory-leak-in-incidents-map
 *
 */
export const useDeckGlContextLossWorkaround =
  (): DeckGlContextLossWorkaround => {
    const glRef = useRef<WebGL2RenderingContext | null>(null);
    const unmountedRef = useRef(false);

    const loseWebGlContext = useCallback((): void => {
      glRef.current?.getExtension('WEBGL_lose_context')?.loseContext();
      glRef.current = null;
    }, []);

    const onDeviceInitialized = useCallback(
      (device: unknown): void => {
        // The device is always a luma.gl WebGLDevice in this app (webgl2Adapter),
        // which exposes its context as `gl`.
        const { gl } = device as { gl?: WebGL2RenderingContext };
        glRef.current = gl ?? null;
        if (unmountedRef.current) {
          // Device creation finished after unmount (fast navigation away); the
          // consumer's cleanup has already run, so lose the context right away.
          loseWebGlContext();
        }
      },
      [loseWebGlContext],
    );

    useEffect(() => {
      unmountedRef.current = false;
      return () => {
        unmountedRef.current = true;
      };
    }, []);

    return { onDeviceInitialized, loseWebGlContext };
  };
